import { ShieldCheckIcon, PencilSquareIcon, TrashIcon, UsersIcon, FolderIcon, EyeIcon } from '@heroicons/react/24/outline';

export interface RoleSummary {
  id: string;
  name: string;
  description?: string | null;
  is_system?: boolean;
  permissions?: string[];
  permission_count?: number;
  group_count?: number;
  group_access?: unknown[];
  user_count?: number;
  [key: string]: unknown;
}

interface RoleTableProps {
  roles: RoleSummary[];
  onView?: (role: RoleSummary) => void;
  onEdit?: (role: RoleSummary) => void;
  onDelete?: (role: RoleSummary) => void;
  /** True when the current user is a super_admin — only they may edit the super_admin role. */
  isSuperAdmin?: boolean;
}

/**
 * Can the current user edit this role? Default (system) roles are editable by
 * anyone with roles.edit (that gate lives in the parent — onEdit is only passed
 * when the user holds it), EXCEPT the super_admin role, which only a super_admin
 * may edit. A non-editable role falls back to a read-only "View".
 */
function canEditRole(role: RoleSummary, isSuperAdmin: boolean): boolean {
  return !(role.name === 'super_admin' && !isSuperAdmin);
}

const ROLE_COLORS: Record<string, { bg: string; text: string; border: string }> = {
  super_admin: { bg: 'bg-red-50 dark:bg-red-900/20', text: 'text-red-700 dark:text-red-400', border: 'border-red-200 dark:border-red-800' },
  admin: { bg: 'bg-blue-50 dark:bg-blue-900/20', text: 'text-blue-700 dark:text-blue-400', border: 'border-blue-200 dark:border-blue-800' },
  operator: { bg: 'bg-amber-50 dark:bg-amber-900/20', text: 'text-amber-700 dark:text-amber-400', border: 'border-amber-200 dark:border-amber-800' },
  viewer: { bg: 'bg-green-50 dark:bg-green-900/20', text: 'text-green-700 dark:text-green-400', border: 'border-green-200 dark:border-green-800' },
};

const DEFAULT_COLOR = { bg: 'bg-purple-50 dark:bg-purple-900/20', text: 'text-purple-700 dark:text-purple-400', border: 'border-purple-200 dark:border-purple-800' };

export default function RoleTable({ roles, onEdit, onDelete, onView, isSuperAdmin = false }: RoleTableProps) {
  if (!roles || roles.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-16 text-center">
        <ShieldCheckIcon className="h-12 w-12 text-muted-foreground/40 mb-3" />
        <p className="text-[13px] text-muted-foreground">No roles found.</p>
      </div>
    );
  }

  return (
    <div data-tour="roles-list" className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
      {roles.map((role) => {
        const color = ROLE_COLORS[role.name] || DEFAULT_COLOR;
        const editable = canEditRole(role, isSuperAdmin);
        return (
          <div
            key={role.id}
            className={`group relative rounded-xl border ${color.border} ${color.bg} p-5 transition-shadow hover:shadow-md cursor-pointer`}
            onClick={() => onView?.(role)}
          >
            {role.is_system && (
              <span className="absolute top-3 right-3 rounded-full bg-muted px-2 py-0.5 text-[10px] font-semibold text-muted-foreground">
                System
              </span>
            )}

            <div className="flex items-center gap-3 mb-3">
              <div className={`flex h-10 w-10 items-center justify-center rounded-lg ${color.bg} ${color.text}`}>
                <ShieldCheckIcon className="h-5 w-5" />
              </div>
              <div>
                <h3 className={`text-[14px] font-semibold capitalize ${color.text}`}>
                  {role.name?.replace(/_/g, ' ')}
                </h3>
                <p className="text-[11px] text-muted-foreground line-clamp-1">
                  {role.description || 'No description'}
                </p>
              </div>
            </div>

            <div className="flex items-center gap-4 text-[12px] text-muted-foreground mb-4">
              <span className="flex items-center gap-1">
                <ShieldCheckIcon className="h-3.5 w-3.5" />
                {role.permission_count || role.permissions?.length || 0} permissions
              </span>
              <span className="flex items-center gap-1">
                <FolderIcon className="h-3.5 w-3.5" />
                {role.group_count || role.group_access?.length || 0} groups
              </span>
              {role.user_count !== undefined && (
                <span className="flex items-center gap-1">
                  <UsersIcon className="h-3.5 w-3.5" />
                  {role.user_count} users
                </span>
              )}
            </div>

            <div className="flex flex-wrap gap-1 mb-4">
              {(role.permissions || []).slice(0, 5).map((perm) => (
                <span
                  key={perm}
                  className="inline-flex rounded-md bg-card/60 px-1.5 py-0.5 text-[10px] font-medium text-muted-foreground"
                >
                  {perm}
                </span>
              ))}
              {(role.permissions || []).length > 5 && (
                <span className="inline-flex rounded-md bg-card/60 px-1.5 py-0.5 text-[10px] font-medium text-muted-foreground">
                  +{(role.permissions as string[]).length - 5} more
                </span>
              )}
            </div>

            <div className="flex items-center gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
              {onEdit && (
                <button
                  onClick={(e) => { e.stopPropagation(); onEdit(role); }}
                  className="flex items-center gap-1 rounded-lg px-2.5 py-1 text-[11px] font-medium text-brand hover:bg-brand-light transition-colors"
                >
                  {editable ? (
                    <>
                      <PencilSquareIcon className="h-3.5 w-3.5" />
                      Edit
                    </>
                  ) : (
                    <>
                      <EyeIcon className="h-3.5 w-3.5" />
                      View
                    </>
                  )}
                </button>
              )}
              {onDelete && !role.is_system && (
                <button
                  onClick={(e) => { e.stopPropagation(); onDelete(role); }}
                  className="flex items-center gap-1 rounded-lg px-2.5 py-1 text-[11px] font-medium text-destructive hover:bg-destructive/10 transition-colors"
                >
                  <TrashIcon className="h-3.5 w-3.5" />
                  Delete
                </button>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}
